
'use client';

import { cn } from "@/lib/utils";
import React, { useRef, useState, useEffect } from 'react';

type SpeedometerProps = {
  speed: number;
  maxSpeed?: number;
  avgSpeed?: number;
  tripMaxSpeed?: number;
  speedCheckStartSpeed?: number;
  liveAvgSpeedCheck?: number;
  className?: string;
};

const Speedometer = ({ 
  speed: targetSpeed = 0, 
  maxSpeed = 240, 
  avgSpeed = 0,
  tripMaxSpeed = 0,
  speedCheckStartSpeed,
  liveAvgSpeedCheck,
  className 
}: SpeedometerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(300); // Default width
  
  const [displaySpeed, setDisplaySpeed] = useState(0);
  const animationFrameRef = useRef<number>();
  const targetSpeedRef = useRef(0);

  useEffect(() => {
    targetSpeedRef.current = targetSpeed;
  }, [targetSpeed]);
  
  useEffect(() => {
    const animate = () => {
      setDisplaySpeed(currentSpeed => {
        const diff = targetSpeedRef.current - currentSpeed;
        
        if (Math.abs(diff) < 0.1) {
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
          }
          return targetSpeedRef.current;
        }

        const nextSpeed = currentSpeed + diff * 0.08; // Damping factor for smoother animation
        animationFrameRef.current = requestAnimationFrame(animate);
        return nextSpeed;
      });
    };
    
    if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
    }
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetSpeed]);


  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0]) {
        setContainerWidth(entries[0].contentRect.width);
      }
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, []);

  const ticks = [];
  const tickSpacing = 80;
  const rulerWidth = (maxSpeed / 10) * tickSpacing;
  
  const translation = containerWidth / 2 - (displaySpeed / 10) * tickSpacing;

  for (let i = 0; i <= maxSpeed; i += 5) {
    const isMajorTick = i % 10 === 0;
    const position = (i / 10) * tickSpacing;

    const distanceFromCenter = Math.abs(displaySpeed - i);
    const scale = Math.max(0.6, 1.8 - (distanceFromCenter / 40));
    const opacity = Math.max(0.3, 1 - (distanceFromCenter / 80));

    if (isMajorTick) {
      ticks.push(
        <div
          key={`tick-major-${i}`}
          className="absolute h-full flex flex-col items-center justify-end"
          style={{ left: position, transform: `translateX(-50%)` }}
        >
          <span
            className="font-mono font-bold text-foreground transition-all duration-300 ease-out mb-1"
            style={{ 
              transform: `scale(${scale})`, 
              opacity: opacity,
              fontSize: '1.1rem',
              transformOrigin: 'bottom center',
            }}
          >
            {i}
          </span>
          <div className="w-0.5 h-6 bg-muted-foreground" style={{ opacity }}/>
        </div>
      );
    } else {
      ticks.push(
        <div
          key={`tick-minor-${i}`}
          className="absolute h-full flex flex-col items-center justify-end"
          style={{ left: position, transform: `translateX(-50%)` }}
        >
          <div className="w-px h-4 bg-muted-foreground/70" style={{ opacity }}/>
        </div>
      );
    }
  }
  
  const Indicator = ({ speedValue, colorClass }: { speedValue: number | undefined; colorClass: string; }) => {
    if (speedValue === undefined || speedValue <= 0) return null;
    const position = (speedValue / 10) * tickSpacing;
    return (
      <div 
        className="absolute bottom-0 h-full flex flex-col-reverse items-center"
        style={{ left: position, transform: 'translateX(-50%)' }}
      >
        <div className={cn("w-1 rounded-t-full", colorClass, "h-8")} />
      </div>
    )
  }

  const constrainedAvgSpeed = tripMaxSpeed > 0 ? Math.min(avgSpeed, tripMaxSpeed) : avgSpeed;

  return (
    <div className={cn("relative w-full flex flex-col items-center justify-center", className)}>
        <div ref={containerRef} className="relative w-full h-24 overflow-hidden">
            <div
                className="absolute top-0 left-0 h-full"
                style={{
                    width: rulerWidth,
                    transform: `translateX(${translation}px)`,
                    willChange: 'transform'
                }}
            >
                {ticks}
                {/* Trip-wide indicators */}
                <Indicator speedValue={constrainedAvgSpeed} colorClass="bg-yellow-400" />
                <Indicator speedValue={tripMaxSpeed} colorClass="bg-red-500" />
                
                {/* Live speed check indicators */}
                <Indicator speedValue={speedCheckStartSpeed} colorClass="bg-green-500" />
                <Indicator speedValue={liveAvgSpeedCheck} colorClass="bg-violet-400" />
            </div>
            
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-full flex flex-col-reverse items-center">
                <div className="h-8 w-1 bg-primary rounded-t-full shadow-lg" />
            </div>
        </div>
    </div>
  );
};

export default Speedometer;
